import { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useUser } from '../App'
import Icon, { type IconName } from './Icon'
import {
  isNotifMuted,
  setNotifMuted,
  browserNotifPermission,
  requestBrowserNotif,
  disableBrowserNotif,
  browserNotifEnabled,
} from '../utils/notifSound'
import { useTurnoCaixa } from '../hooks/useTurnoCaixa'
import FecharCaixaModal from './FecharCaixaModal'
import CaixaMovimentacaoModal from './CaixaMovimentacaoModal'

/**
 * NavBar — header sticky (mobile) + sidebar fixa (desktop, D-96 iter 5, 2026-05-15).
 *
 * Mobile (< md):  barra superior terracota + drawer (hamburger).
 * Desktop (≥ md): sidebar fixa à esquerda com seções sempre visíveis.
 *
 * Truque pra não refatorar 14 páginas: o componente seta
 * `body[data-layout="sidebar"]` no mount; CSS em `index.css` adiciona
 * `padding-left: 16rem` no body em md+, abrindo espaço pra sidebar.
 * Logout (NavBar unmount) limpa o attr → padding some.
 *
 * 5 seções: Painel · Operação · Delivery · Financeiro · Configuração.
 * Permissões D-96: Equipe/Cardápio editar/Extras = admin-only.
 */
type Secao = 'painel' | 'operacao' | 'delivery' | 'financeiro' | 'config'
interface Link {
  secao: Secao
  path: string
  label: string
  icon: IconName
  perfis: string[]
}

const LINKS: Link[] = [
  // Painel (admin)
  { secao: 'painel',    path: '/admin',                              label: 'Painel',           icon: 'home',     perfis: ['admin'] },

  // D-160 (25/05): Copa — KDS read-only. Perfil 'copa' só vê esse link.
  { secao: 'operacao',  path: '/copa',                               label: 'Copa',             icon: 'bebida',   perfis: ['copa', 'admin'] },

  // Operação
  { secao: 'operacao',  path: '/mesas',                              label: 'Mesas',            icon: 'mesa',     perfis: ['atendente', 'caixa', 'admin'] },
  { secao: 'operacao',  path: '/reservas',                           label: 'Reservas',         icon: 'reserva',  perfis: ['atendente', 'caixa', 'admin'] },
  { secao: 'operacao',  path: '/saida',                              label: 'Saída',            icon: 'saida',    perfis: ['seguranca', 'caixa', 'admin'] },
  { secao: 'operacao',  path: '/menu',                               label: 'Cardápio',         icon: 'menu',     perfis: ['atendente', 'caixa', 'admin'] },
  // D-104: Balcão (avulso) saiu do NavBar e virou card embutido na tela de Mesas.
  // Rota /avulso continua existindo como fallback de URL direto.

  // Delivery
  { secao: 'delivery',  path: '/admin/delivery',                     label: 'Painel delivery',  icon: 'delivery', perfis: ['admin'] },
  { secao: 'delivery',  path: '/admin/delivery/pedidos',             label: 'Pedidos delivery', icon: 'pedidos',  perfis: ['admin', 'caixa'] },
  { secao: 'delivery',  path: '/admin/delivery/historico',           label: 'Hist. delivery',   icon: 'historico', perfis: ['admin', 'caixa'] },
  { secao: 'delivery',  path: '/admin/delivery/cardapio-do-dia',     label: 'Cardápio do dia',  icon: 'menu-day', perfis: ['admin'] },
  { secao: 'delivery',  path: '/admin/delivery/bebidas',             label: 'Bebidas',          icon: 'bebida',   perfis: ['admin'] },
  { secao: 'delivery',  path: '/admin/delivery/marmitas',            label: 'Marmitas',         icon: 'marmita',  perfis: ['admin'] },
  { secao: 'delivery',  path: '/admin/delivery/adicionais',          label: 'Adicionais',       icon: 'extras',   perfis: ['admin'] },
  { secao: 'delivery',  path: '/admin/delivery/impressao',           label: 'Fila impressão',   icon: 'pedidos',  perfis: ['admin', 'caixa'] },

  // Financeiro
  { secao: 'financeiro', path: '/historico',                         label: 'Histórico',        icon: 'historico', perfis: ['caixa', 'admin'] },
  { secao: 'financeiro', path: '/admin/vales',                       label: 'Vales',            icon: 'vales',     perfis: ['admin'] },
  { secao: 'financeiro', path: '/meus-vales',                        label: 'Meus vales',       icon: 'vales',     perfis: ['atendente'] },
  { secao: 'financeiro', path: '/extras',                            label: 'Minhas gorjetas',  icon: 'extras',    perfis: ['atendente'] },

  // Configuração (admin only — caixa removido em D-96)
  // D-161 (25/05): admin gerencia QR codes (gerar/imprimir/regenerar)
  { secao: 'config',    path: '/admin/qr-codes',                     label: 'QR codes mesas',    icon: 'menu',     perfis: ['admin'] },
  { secao: 'config',    path: '/cardapio',                           label: 'Cardápio (editar)', icon: 'edit',    perfis: ['admin'] },
  { secao: 'config',    path: '/equipe',                             label: 'Equipe',           icon: 'equipe',   perfis: ['admin'] },
  { secao: 'config',    path: '/extras',                             label: 'Extras',           icon: 'extras',   perfis: ['admin'] },
  // D-110: trocar minha senha — só caixa/admin (atendente/seguranca/copa logam sem senha)
  { secao: 'config',    path: '/trocar-senha',                       label: 'Trocar senha',     icon: 'edit',     perfis: ['caixa', 'admin'] },
]

const SECAO_LABELS: Record<Secao, string> = {
  painel: '',
  operacao: 'Operação',
  delivery: 'Delivery',
  financeiro: 'Financeiro',
  config: 'Configuração',
}

const SECAO_ORDER: Secao[] = ['painel', 'operacao', 'delivery', 'financeiro', 'config']

export default function NavBar() {
  const { user, logout } = useUser()
  const nav = useNavigate()
  const loc = useLocation()
  const [open, setOpen] = useState(false)
  const [muted, setMutedState] = useState(() => isNotifMuted())
  const [browserEnabled, setBrowserEnabled] = useState(() => browserNotifEnabled())
  const [browserPerm, setBrowserPerm] = useState<NotificationPermission | 'unsupported'>(() => browserNotifPermission())
  const menuRef = useRef<HTMLDivElement>(null)

  function toggleMute() {
    const next = !muted
    setNotifMuted(next)
    setMutedState(next)
  }

  async function toggleBrowserNotif() {
    if (browserEnabled) {
      disableBrowserNotif()
      setBrowserEnabled(false)
      return
    }
    const result = await requestBrowserNotif()
    setBrowserPerm(result === 'unsupported' ? 'unsupported' : result)
    setBrowserEnabled(result === 'granted')
  }

  // D-96: notificações só pra caixa/admin (atendente não opera delivery)
  const podeReceberNotif = !!user && (user.perfil === 'caixa' || user.perfil === 'admin')

  // D-102: turno de caixa só pra caixa (admin opera pela operação normal sem turno)
  const podeOperarCaixa = !!user && user.perfil === 'caixa'
  const turnoState = useTurnoCaixa(podeOperarCaixa)
  const [showFecharModal, setShowFecharModal] = useState(false)
  // D-119: modal de Sangria/Suprimento
  const [showMovModal, setShowMovModal] = useState(false)

  // D-96 iter 5: sidebar fixa em desktop — body ganha padding-left via CSS
  useEffect(() => {
    if (!user) return
    document.body.dataset.layout = 'sidebar'
    return () => { delete document.body.dataset.layout }
  }, [user])

  // Click-outside fecha drawer mobile
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // ESC fecha o drawer
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  // Fecha drawer mobile ao navegar
  useEffect(() => { setOpen(false) }, [loc.pathname])

  if (!user) return null

  const visible = LINKS.filter(l => l.perfis.includes(user.perfil))
  const visibleBySection = SECAO_ORDER
    .map(secao => ({ secao, items: visible.filter(l => l.secao === secao) }))
    .filter(g => g.items.length > 0)

  const homePath = user.perfil === 'admin' ? '/admin'
    : user.perfil === 'copa' ? '/copa'
    : user.perfil === 'seguranca' ? '/saida'
    : '/mesas'

  // === Render compartilhado: lista de seções com itens (usado em drawer mobile + sidebar desktop) ===
  const renderSecoes = (variant: 'drawer' | 'sidebar') => (
    <nav className="py-1" aria-label="Navegação principal">
      {visibleBySection.map(({ secao, items }, idx) => (
        <div key={secao}>
          {SECAO_LABELS[secao] && (
            <p className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider text-gray-400 font-semibold">
              {SECAO_LABELS[secao]}
            </p>
          )}
          {items.map(l => {
            const active = isActive(loc.pathname, l.path)
            return (
              <button
                key={l.path}
                onClick={() => { nav(l.path); if (variant === 'drawer') setOpen(false) }}
                className={`w-full text-left flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                  active
                    ? 'bg-terracota/8 text-terracota font-semibold border-l-2 border-terracota -ml-px pl-[15px]'
                    : 'hover:bg-cream-dark/30 text-gray-800'
                }`}
                role="menuitem"
              >
                <Icon name={l.icon} size={16} className={active ? 'text-terracota' : 'text-gray-500'} />
                <span className="flex-1">{l.label}</span>
              </button>
            )
          })}
          {idx < visibleBySection.length - 1 && (
            <div className="my-1 mx-4 border-b border-cream-border/60" />
          )}
        </div>
      ))}
    </nav>
  )

  const renderUserHeader = () => (
    <div className="px-4 pt-4 pb-3 border-b border-cream-border bg-cream/60">
      <button
        onClick={() => nav(homePath)}
        className="font-serif text-lg font-semibold text-carvao hover:text-terracota transition-colors"
        title="Início"
      >
        Myro&rsquo;s
      </button>
      <p className="text-[10px] uppercase tracking-widest text-terracota/70 mt-1">
        {user.perfil}
      </p>
      <p className="text-xs text-gray-600 mt-0.5 truncate">{user.nome}</p>
    </div>
  )

  const renderNotifToggles = () => podeReceberNotif && (
    <div className="border-t border-cream-border bg-white">
      <button
        onClick={toggleMute}
        className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-cream-dark/30 transition"
        title={muted ? 'Clique para ativar o som' : 'Clique para silenciar o som'}
      >
        <Icon name="bell" size={16} className={muted ? 'text-gray-400' : 'text-terracota'} />
        <span className="flex-1">Som das notificações</span>
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${muted ? 'bg-gray-100 text-gray-500' : 'bg-terracota/10 text-terracota'}`}>
          {muted ? 'mudo' : 'ativo'}
        </span>
      </button>
      {browserPerm !== 'unsupported' && (
        <button
          onClick={toggleBrowserNotif}
          disabled={browserPerm === 'denied'}
          className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-cream-dark/30 transition disabled:opacity-50 disabled:cursor-not-allowed"
          title={
            browserPerm === 'denied'
              ? 'Permissão negada — habilite manualmente nas configurações do navegador'
              : browserEnabled
                ? 'Clique para desativar notificações do sistema'
                : 'Clique para receber notificações no canto do sistema quando esta aba estiver minimizada'
          }
        >
          <Icon name="bell" size={16} className={browserEnabled ? 'text-terracota' : 'text-gray-400'} />
          <span className="flex-1">Notif. do sistema</span>
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${browserEnabled ? 'bg-terracota/10 text-terracota' : 'bg-gray-100 text-gray-500'}`}>
            {browserPerm === 'denied' ? 'bloqueada' : browserEnabled ? 'ativa' : 'desativada'}
          </span>
        </button>
      )}
    </div>
  )

  const renderLogout = (variant: 'drawer' | 'sidebar') => (
    <div className="border-t border-cream-border bg-cream/40">
      {/* D-119: Sangria/Suprimento — só pra perfil caixa quando turno aberto */}
      {podeOperarCaixa && turnoState.aberto && (
        <button
          onClick={() => { setShowMovModal(true); if (variant === 'drawer') setOpen(false) }}
          className="w-full text-left flex items-center gap-3 px-4 py-3 text-sm text-carvao hover:bg-cream-dark/30 transition border-b border-cream-border"
        >
          <Icon name="vales" size={16} className="text-gray-500" />
          <span>Sangria / Suprimento</span>
        </button>
      )}
      {/* D-102: Fechar caixa — só pra perfil caixa quando turno aberto */}
      {podeOperarCaixa && turnoState.aberto && (
        <button
          onClick={() => { setShowFecharModal(true); if (variant === 'drawer') setOpen(false) }}
          className="w-full text-left flex items-center gap-3 px-4 py-3 text-sm text-red-700 font-medium hover:bg-red-50 transition border-b border-cream-border"
        >
          <Icon name="saida" size={16} className="text-red-600" />
          <span>Fechar caixa</span>
        </button>
      )}
      <button
        onClick={() => { logout(); nav('/login'); if (variant === 'drawer') setOpen(false) }}
        className="w-full text-left flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-cream-dark/30 transition"
      >
        <Icon name="logout" size={16} className="text-gray-500" />
        <span>Sair</span>
      </button>
    </div>
  )

  return (
    <>
      {/* ─── Mobile (< md): top bar + drawer ───────────────────────────── */}
      {/* D-115: glass-header — gradiente vinho sutil */}
      <div
        ref={menuRef}
        className="md:hidden glass-header text-white flex items-center justify-between px-3 py-2 sticky top-0 z-40 relative"
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => setOpen(!open)}
            className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-white/10 transition text-white"
            aria-label="Menu"
            aria-expanded={open}
          >
            <Icon name={open ? 'close' : 'hamburger'} size={20} />
          </button>
          <button
            onClick={() => nav(homePath)}
            className="font-serif text-base font-semibold tracking-tight-1 hover:opacity-80 transition"
            title="Início"
          >
            Myro&rsquo;s
          </button>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs opacity-90 hidden sm:inline">{user.nome}</span>
          <button onClick={() => { logout(); nav('/login') }} className="text-xs bg-white/20 px-2.5 py-1 rounded-md hover:bg-white/30 transition">
            Sair
          </button>
        </div>

        {open && (
          <div
            className="absolute top-full left-0 bg-white text-carvao rounded-b-xl shadow-lg w-[280px] max-h-[80vh] overflow-y-auto z-50"
            role="menu"
          >
            {renderUserHeader()}
            {renderSecoes('drawer')}
            {renderNotifToggles()}
            {renderLogout('drawer')}
          </div>
        )}
      </div>

      {/* ─── Desktop (≥ md): sidebar fixa ───────────────────────────────── */}
      {/* D-115: glass-sidebar — branco gelo com leve blur, sem perder leitura */}
      <aside
        className="hidden md:flex md:fixed md:inset-y-0 md:left-0 md:w-64 md:flex-col glass-sidebar md:z-40"
        aria-label="Navegação lateral"
      >
        <div className="shrink-0">{renderUserHeader()}</div>
        <div className="flex-1 overflow-y-auto min-h-0">{renderSecoes('sidebar')}</div>
        <div className="shrink-0">
          {renderNotifToggles()}
          {renderLogout('sidebar')}
        </div>
      </aside>

      {/* D-102: Modal de fechamento de caixa */}
      <FecharCaixaModal
        open={showFecharModal}
        onClose={() => setShowFecharModal(false)}
        onFechado={() => {
          // Caixa fechado → recibo já impresso pelo modal. Faz logout e volta pro login.
          setShowFecharModal(false)
          logout()
          nav('/login', { replace: true })
        }}
      />

      {/* D-119: Modal de Sangria/Suprimento */}
      <CaixaMovimentacaoModal
        open={showMovModal}
        onClose={() => setShowMovModal(false)}
      />
    </>
  )
}

/** Match exato OU prefixo seguido de / pra evitar /admin casar com /admin/vales. */
function isActive(currentPath: string, linkPath: string): boolean {
  if (currentPath === linkPath) return true
  // /admin não pode casar com /admin/vales (matchamos só /admin exato como Painel)
  if (linkPath === '/admin') return currentPath === '/admin' || currentPath === '/admin/dashboard'
  return currentPath.startsWith(linkPath + '/')
}
